import React from 'react'
import {
  getOutletFormattedAddress,
  getOutletDeliveryFee,
  isOutletAcceptingPreOrders,
} from '@ancon/wildcat-utils/outlet'
import { getFormattedCurrency } from '@ancon/wildcat-utils/currency'
import InfoIcon from '@ancon/wildcat-ui/shared/icons/info.svg'
import OutletBannerPlaceholder from '@ancon/wildcat-ui/shared/static/placeholders/outlet-top-banner.svg'
import { OutletState } from '@ancon/wildcat-types'
import useTranslation from 'next-translate/useTranslation'
import clsx from 'clsx'

import useAppSelector from '../../../store/hooks/useAppSelector'
import {
  outletFiltersOrderFormatSelector,
  outletListItemAvailableOrderFormatsSelector,
  outletListItemSelector,
} from '../store/outletSelector'
import HeadingText from '../../app/components/HeadingText'
import BodyText from '../../app/components/BodyText'
import OrderTypeIcon from '../../app/components/OrderTypeIcon'
import Button from '../../app/components/Button'
import useAppDispatch from '../../../store/hooks/useAppDispatch'
import { outletSetIsVisibleOutletDetailsModal } from '../store/outletSlice'
import isAppWhiteLabeled from '../../app/utils/isAppWhiteLabeled'
import isOutletDeliveryFeeShown from '../utils/isOutletDeliveryFeeShown'
import OptimizedImage from '../../app/components/OptmizedImage'

import OutletOrderPreparationTimeTag from './OutletOrderPreparationTimeTag'
import styles from './OutletDetailsHeader.module.scss'

type OutletHeaderOrderFormatsProps = {
  marginBottom?: boolean
}

function OutletHeaderOrderFormats({
  marginBottom,
}: OutletHeaderOrderFormatsProps) {
  const orderFormats = useAppSelector(
    outletListItemAvailableOrderFormatsSelector,
  )

  return (
    <div
      className={clsx(styles.orderFormats, {
        [styles.marginBottom]: marginBottom,
      })}
    >
      {orderFormats.map(format => (
        <OrderTypeIcon key={format} orderFormat={format} size={48} />
      ))}
    </div>
  )
}

export default function OutletDetailsHeader() {
  const dispatch = useAppDispatch()
  const outlet = useAppSelector(outletListItemSelector)
  const isWhiteLabeled = isAppWhiteLabeled()

  const { t } = useTranslation('outlet')

  const {
    name,
    address,
    imageId,
    logoId,
    serviceFees,
    orderFormatSettings,
    state,
  } = outlet!

  const deliveryFee = getOutletDeliveryFee(serviceFees)

  const isAcceptingPreOrders = isOutletAcceptingPreOrders(orderFormatSettings)
  const isOpened = state === OutletState.Opened
  const isAcceptingOfflineOrders = !isOpened && isAcceptingPreOrders
  const isCaution = !isAcceptingOfflineOrders && !isOpened

  const orderFormat = useAppSelector(outletFiltersOrderFormatSelector)

  const showDeliveryFee = isOutletDeliveryFeeShown(outlet!)

  function getOutletOpenStateText() {
    if (isOpened) {
      return t('open')
    }
    if (isAcceptingOfflineOrders) {
      return t('acceptingPreOrders')
    }
    return t('closed')
  }

  function handleMoreInfoClicked() {
    dispatch(outletSetIsVisibleOutletDetailsModal(true))
  }

  return (
    <div
      className={clsx(styles.container, {
        [styles.whiteLabeled]: isWhiteLabeled,
      })}
    >
      {!isWhiteLabeled && (
        <div className={styles.imgContainer}>
          <OptimizedImage
            mediaId={imageId}
            src={OutletBannerPlaceholder}
            alt={`${name} banner`}
            className={styles.bannerImage}
            height={186}
            width={280}
            priority
          />
          <OptimizedImage
            mediaId={logoId}
            src="/images/outlet-logo-placeholder.jpg"
            alt={`${name} logo`}
            className={styles.logoImage}
            height={100}
            width={100}
            priority
          />
        </div>
      )}
      <div className={styles.detailsContainer}>
        <div>
          <div className={styles.headerWrapper}>
            <HeadingText as="h2" title={name}>
              {name}
            </HeadingText>

            <div className={styles.rightActions}>
              {isWhiteLabeled && (
                <Button
                  variant="secondary"
                  onClick={handleMoreInfoClicked}
                  className={styles.outletInfoButton}
                  outlined
                >
                  <InfoIcon height={16} width={16} />
                  <span className={styles.infoButtonText}>{t('moreInfo')}</span>
                </Button>
              )}
            </div>
          </div>
          <BodyText
            fontSize="1.6rem"
            color="body-1"
            title={getOutletFormattedAddress(address)}
            className={styles.outletAddress}
          >
            {getOutletFormattedAddress(address)}
          </BodyText>
          <div className={styles.outletStateInfo}>
            <div className={styles.deliveryStatusRow}>
              <BodyText
                className={clsx({
                  [styles.dangerText]: isCaution,
                  [styles.successText]: !isCaution,
                })}
              >
                {getOutletOpenStateText()}
              </BodyText>
              {showDeliveryFee && (
                <BodyText className={styles.deliveryInfoText} color="body-1">
                  {deliveryFee?.amount
                    ? t('deliveryFee', {
                        amount: getFormattedCurrency(
                          deliveryFee.amount.amount,
                          deliveryFee.amount.currency,
                        ),
                      })
                    : t('freeDelivery')}
                </BodyText>
              )}
              {isWhiteLabeled && <OutletHeaderOrderFormats />}
            </div>
            <OutletOrderPreparationTimeTag
              outlet={outlet}
              orderFormat={orderFormat}
            />
          </div>
        </div>
        <div className={styles.detailsBottomRow}>
          {!isWhiteLabeled && <OutletHeaderOrderFormats marginBottom />}

          <div className={styles.outletActionsContainer}>
            {!isWhiteLabeled && (
              <Button
                variant="secondary"
                onClick={handleMoreInfoClicked}
                className={styles.outletInfoButton}
                outlined
              >
                <InfoIcon height={16} width={16} />
                <span className={styles.infoButtonText}>{t('moreInfo')}</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
